import { useState } from 'react';
import { Video } from 'lucide-react';
import { format } from 'date-fns';
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { IntruderVideoClipPlayer } from '@/app/Alert/components/IntruderVideoClipPlayer';
import { IntruderVideoClip } from '@/app/Alert/types';

interface IncidentVideoSectionProps {
  videoClip: IntruderVideoClip;
}

export function IncidentVideoSection({ videoClip }: IncidentVideoSectionProps) {
  const [showVideoClipModal, setShowVideoClipModal] = useState(false);

  return (
    <>
      <AccordionItem value="video-clip">
        <AccordionTrigger className="p-4 font-semibold">
          <div className="flex items-center gap-2">
            <span>Incident Video</span>
          </div>
        </AccordionTrigger>
        <AccordionContent className="border-t">
          <div className="p-4">
            <IntruderVideoClipPlayer
              videoClip={videoClip}
              onExpand={() => setShowVideoClipModal(true)}
            />
          </div>
        </AccordionContent>
      </AccordionItem>

      {/* Video Clip Popout Dialog */}
      <Dialog open={showVideoClipModal} onOpenChange={setShowVideoClipModal}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Video className="h-5 w-5 text-lime" />
              Incident Video Clip
            </DialogTitle>
          </DialogHeader>
          <div className="mt-2">
            {videoClip.status === 'Ready' && videoClip.cloudflareEmbedUrl ? (
              <div className="overflow-hidden rounded-lg border border-[#2E3835] bg-[#1b1b1b]">
                {/* Description */}
                <div className="border-b border-[#2E3835] bg-[#222] px-3 py-2">
                  <p className="text-xs text-gray-400">
                    This is the representation of the incident that the agent picked.
                  </p>
                </div>

                <div className="aspect-video">
                  <iframe
                    src={videoClip.cloudflareEmbedUrl}
                    className="h-full w-full border-none"
                    allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture"
                    allowFullScreen
                  />
                </div>
                <div className="border-t border-[#2E3835] p-3">
                  {videoClip.startTime && videoClip.endTime && (
                    <p className="text-sm text-gray-400">
                      {format(new Date(videoClip.startTime), 'dd MMM yyyy HH:mm:ss')} -{' '}
                      {format(new Date(videoClip.endTime), 'dd MMM yyyy HH:mm:ss')}
                    </p>
                  )}
                  {videoClip.expiresAt && (
                    <p className="mt-1 text-xs text-gray-500">
                      Video expires: {format(new Date(videoClip.expiresAt), 'dd MMM yyyy')}
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <IntruderVideoClipPlayer videoClip={videoClip} />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
